import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Text,
  Button,
  PlainButton,
  Icon,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Spinner,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { useCopilotHandleDownload } from '@/features/copilot/hooks/use-copilot-handle-download';
import { useCopilotImageThumbnail } from '@/features/copilot/hooks/use-copilot-image-thumbnail';
import type { CopilotSession } from '@customerio/hydra-copilot';
import * as styles from './index.css';
import { isEmptyString } from '@/utils/string';

export interface FileData {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  source: File | string;
  status?: 'pending' | 'uploading' | 'uploaded' | 'error';
}

interface FileChipListProps {
  files: Array<FileData>;
  readOnly?: boolean;
  onRemove?: (fileId: string) => void;
  onShowFile?: (fileId: string) => void;
  session?: CopilotSession;
}

export function FileChipList({ files, readOnly = false, onRemove, onShowFile, session }: FileChipListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(files.length);

  useEffect(() => {
    if (containerRef.current == null || files.length === 0) return;

    const calculateVisibleFiles = () => {
      const container = containerRef.current;
      if (container == null) return;

      const containerWidth = container.offsetWidth;
      const gap = 8; // gap="100" translates to 8px
      const overflowChipWidth = 60; // Approximate width of "+X" chip

      // Get all rendered chip elements
      const chipElements = container.querySelectorAll('[data-file-chip]');

      if (chipElements.length === 0) {
        // Chips haven't rendered yet, try again on next frame
        requestAnimationFrame(calculateVisibleFiles);
        return;
      }

      const availableWidth = containerWidth;
      let currentWidth = 0;
      let count = 0;

      for (let i = 0; i < chipElements.length; i++) {
        const chip = chipElements[i] as HTMLElement;
        const chipWidth = chip.offsetWidth;
        const isLast = i === chipElements.length - 1;

        // Width needed for this chip plus gap (if not last)
        const widthNeeded = chipWidth + (isLast ? 0 : gap);

        // Check if we can fit this chip
        if (currentWidth + widthNeeded > availableWidth) {
          // Can't fit this chip, break
          break;
        }

        // Check if we need to leave room for overflow chip
        const remainingFiles = files.length - (i + 1);
        if (remainingFiles > 0) {
          // We have more files, check if we have room for overflow chip
          if (currentWidth + widthNeeded + gap + overflowChipWidth > availableWidth) {
            // No room for overflow chip after this one, stop here
            break;
          }
        }

        currentWidth += widthNeeded;
        count++;
      }

      // Always show at least 1 file
      const newCount = Math.max(1, count);
      if (newCount !== visibleCount) {
        setVisibleCount(newCount);
      }
    };

    // Initial calculation with a small delay to ensure chips are rendered
    const timer = setTimeout(calculateVisibleFiles, 0);

    // Recalculate on resize of container
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleFiles();
    });

    resizeObserver.observe(containerRef.current);

    // Also observe each chip for size changes (e.g., when images load)
    const chipElements = containerRef.current.querySelectorAll('[data-file-chip]');
    for (const chip of chipElements) {
      resizeObserver.observe(chip as HTMLElement);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [files.length, visibleCount]);

  if (files.length === 0) {
    return null;
  }

  const overflowFiles = files.slice(visibleCount);
  const hasOverflow = overflowFiles.length > 0;

  return (
    <Box
      ref={containerRef}
      className={readOnly ? styles.fileChipListReadOnly : styles.fileChipList}
      display="flex"
      flexWrap="nowrap"
      gap="100"
      mb="100"
      backgroundColor={readOnly ? undefined : 'base'}
      style={{ position: 'relative' }}
    >
      {files.map((file, index) => (
        <Box
          key={file.id}
          style={{
            visibility: index < visibleCount ? 'visible' : 'hidden',
            position: index < visibleCount ? 'relative' : 'absolute',
            pointerEvents: index < visibleCount ? 'auto' : 'none',
          }}
        >
          <FileChip file={file} readOnly={readOnly} onRemove={onRemove} onClick={onShowFile} session={session} />
        </Box>
      ))}
      {hasOverflow ? (
        <OverflowChip
          files={overflowFiles}
          readOnly={readOnly}
          onRemove={onRemove}
          onShowFile={onShowFile}
          session={session}
        />
      ) : null}
    </Box>
  );
}

interface OverflowChipProps {
  files: Array<FileData>;
  readOnly?: boolean;
  onRemove?: (fileId: string) => void;
  onShowFile?: (fileId: string) => void;
  session?: CopilotSession;
}

function OverflowChip({ files, readOnly = false, onRemove, onShowFile, session }: OverflowChipProps) {
  return (
    <Popover placement="top" shouldTriggerOnClick={true} shouldTriggerOnHover={false}>
      <PopoverTrigger>
        <Box
          className={`${styles.fileChip} ${styles.overflowChip} ${readOnly ? styles.overflowChipReadOnly : ''}`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px="100"
          backgroundColor={readOnly ? 'active' : 'base'}
          data-status={readOnly ? 'default' : 'pending'}
          style={{ cursor: 'pointer' }}
        >
          <Text size="s" weight="medium" color="base">
            +{files.length}
          </Text>
        </Box>
      </PopoverTrigger>

      <PopoverContent>
        <Box className={styles.overflowList} display="flex" flexDirection="column" gap="050">
          {files.map((file) => (
            <OverflowFileItem
              key={file.id}
              file={file}
              readOnly={readOnly}
              onRemove={onRemove}
              onShowFile={onShowFile}
              session={session}
            />
          ))}
        </Box>
      </PopoverContent>
    </Popover>
  );
}

interface OverflowFileItemProps {
  file: FileData;
  readOnly?: boolean;
  onRemove?: (fileId: string) => void;
  onShowFile?: (fileId: string) => void;
  session?: CopilotSession;
}

function OverflowFileItem({ file, readOnly = false, onRemove, onShowFile, session }: OverflowFileItemProps) {
  const isImage = file.mimeType.startsWith('image/');
  const isUploading = !readOnly && (file.status === 'uploading' || file.status === 'pending');

  const thumbnailUrl = useCopilotImageThumbnail({
    source: file.source,
    isImage,
    readOnly,
    session,
  });

  // Use the blob URL if available (for images), otherwise use the original source
  const downloadSource = !isEmptyString(thumbnailUrl) ? thumbnailUrl : file.source;

  const { handleDownload } = useCopilotHandleDownload({
    source: downloadSource,
    filename: file.name,
    session,
  });

  const getFileIcon = (mimeType: string): IconName => {
    if (mimeType.startsWith('image/')) return 'image' as IconName;
    if (mimeType === 'application/pdf') return 'description' as IconName;
    if (mimeType.startsWith('text/')) return 'description' as IconName;
    return 'attach-file' as IconName;
  };

  return (
    <Box
      className={`${styles.overflowItem} ${readOnly ? styles.overflowItemReadOnly : ''}`}
      display="flex"
      alignItems="center"
      gap="050"
      p="050"
    >
      {isImage && !isEmptyString(thumbnailUrl) ? (
        <Box className={styles.thumbnail} flexShrink="0" backgroundColor="base" overflow="hidden" position="relative">
          <Box as="img" src={thumbnailUrl} alt={file.name} w="full" h="full" className={styles.thumbnailImage} />
          {isUploading ? (
            <Box
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
            >
              <Spinner />
            </Box>
          ) : null}
        </Box>
      ) : (
        <Icon name={getFileIcon(file.mimeType)} size="sm" color="base" />
      )}

      <Box flexGrow="1" style={{ minWidth: 0 }}>
        {onShowFile != null || readOnly ? (
          <PlainButton
            display="block"
            textAlign="left"
            onClick={() => {
              if (readOnly) {
                handleDownload();
              } else if (onShowFile != null) {
                onShowFile(file.id);
              }
            }}
          >
            <Text size="s" weight="medium" truncate title={file.name}>
              {file.name}
            </Text>
          </PlainButton>
        ) : (
          <Text size="s" weight="medium" truncate title={file.name}>
            {file.name}
          </Text>
        )}
      </Box>

      {!readOnly && onRemove != null ? (
        <Button
          variant="subtle"
          size="sm"
          isIconOnly
          icon="close"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(file.id);
          }}
        />
      ) : null}
    </Box>
  );
}

interface FileChipProps {
  file: FileData;
  readOnly?: boolean;
  onRemove?: (fileId: string) => void;
  onClick?: (fileId: string) => void;
  session?: CopilotSession;
}

export function FileChip({ file, readOnly = false, onRemove, onClick, session }: FileChipProps) {
  const { id, name, mimeType, source, status = 'uploaded' } = file;
  const [preloadedImageUrl, setPreloadedImageUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  const isImage = mimeType.startsWith('image/');

  const imageUrl = useCopilotImageThumbnail({
    source,
    isImage,
    readOnly,
    session,
  });

  // Use the blob URL if available (for images), otherwise use the original source
  const downloadSource = !isEmptyString(imageUrl) ? imageUrl : source;

  const { handleDownload } = useCopilotHandleDownload({
    source: downloadSource,
    filename: file.name,
    session,
  });

  const getFileIcon = (mimeType: string): IconName => {
    if (mimeType.startsWith('image/')) return 'image' as IconName;
    if (mimeType === 'application/pdf') return 'description' as IconName;
    if (mimeType.startsWith('text/')) return 'description' as IconName;
    return 'attach-file' as IconName;
  };

  // Preload the preview image so popover can calculate correct dimensions
  useEffect(() => {
    if (isEmptyString(imageUrl)) {
      setPreloadedImageUrl(null);
      setImageDimensions(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      // Calculate dimensions respecting max constraints
      const maxWidth = 400;
      const maxHeight = 400;
      let width = img.naturalWidth;
      let height = img.naturalHeight;

      // Scale down if needed while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      setImageDimensions({ width, height });
      setPreloadedImageUrl(imageUrl);
    };
    img.onerror = () => {
      // If loading fails, still set it so the popover shows something
      setPreloadedImageUrl(imageUrl);
      setImageDimensions(null);
    };
    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  const handleClick = () => {
    if (readOnly) {
      handleDownload();
    } else if (onClick != null) {
      onClick(id);
    }
  };

  const isUploading = !readOnly && (status === 'uploading' || status === 'pending');

  const chipContent = (
    <Box display="flex" alignItems="center" gap="050" flexGrow="1" style={{ minWidth: 0 }}>
      {isImage && !isEmptyString(imageUrl) ? (
        <Box className={styles.thumbnail} flexShrink="0" backgroundColor="base" overflow="hidden" position="relative">
          <Box as="img" src={imageUrl} alt={name} w="full" h="full" className={styles.thumbnailImage} />
          {isUploading ? (
            <Box
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
            >
              <Spinner />
            </Box>
          ) : null}
        </Box>
      ) : (
        <Icon name={getFileIcon(mimeType)} size="sm" color="base" />
      )}

      <Box style={{ minWidth: 0 }} flexGrow="1">
        <Text size="s" weight="medium" truncate title={name}>
          {isImage ? 'Image' : name}
        </Text>
      </Box>
    </Box>
  );

  return (
    <Box
      className={`${styles.fileChip} ${readOnly ? styles.fileChipReadOnly : ''}`}
      display="flex"
      alignItems="center"
      gap="025"
      pl="050"
      backgroundColor={readOnly ? 'active' : 'base'}
      data-status={readOnly ? 'default' : 'pending'}
      data-file-chip
    >
      {isImage && !isEmptyString(preloadedImageUrl) ? (
        <Popover
          placement="top"
          shouldTriggerOnClick={false}
          shouldTriggerOnHover={true}
          floatingPluginOptions={{
            hover: {
              enabled: true,
              delay: 500,
            },
          }}
        >
          <PopoverTrigger cursor={readOnly || onClick != null ? 'pointer' : 'default'} onClick={handleClick}>
            {chipContent}
          </PopoverTrigger>

          <PopoverContent>
            <Box className={styles.previewContent}>
              <Box
                as="img"
                src={preloadedImageUrl}
                alt={name}
                className={styles.previewImage}
                style={
                  imageDimensions != null
                    ? { width: `${imageDimensions.width}px`, height: `${imageDimensions.height}px` }
                    : undefined
                }
              />
              <Text size="xs" color="base" className={styles.previewFilename}>
                {name}
              </Text>
            </Box>
          </PopoverContent>
        </Popover>
      ) : onClick != null ? (
        <PlainButton display="flex" cursor={'pointer'} onClick={handleClick}>
          {chipContent}
        </PlainButton>
      ) : (
        chipContent
      )}
      {!readOnly && onRemove != null ? (
        <Tooltip isCustom content="Remove file">
          {(context: unknown) => (
            <Button
              variant="subtle"
              size="sm"
              isIconOnly
              icon="close"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
            />
          )}
        </Tooltip>
      ) : null}
    </Box>
  );
}
